import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Gear, Sector, Stage, TOOL_NAMES } from '../domain/olmps';
import { makeThemedStyles, radii } from '../v10/theme';
import { Hexagon } from './primitives';
import { ToolHost } from './ToolHost';
import { Atmosphere } from './glass';
import { DeityInterface } from './DeityInterface';
import { getDeityAvatar } from './deityAvatars';

export function ToolPanel({
  sector,
  stage,
  gear,
  toolIds,
  activeToolIndex,
  onSelectToolIndex,
  onClose,
  onOpenInfo,
  toolProps,
  onOpenTool,
}: {
  sector: Sector;
  stage: Stage;
  gear: Gear;
  toolIds: string[];
  activeToolIndex: number;
  onSelectToolIndex: (index: number) => void;
  onClose: () => void;
  onOpenInfo: () => void;
  /** Narrow, additive per-tool preselection (e.g. THEMIS's initialProtocolId
   *  — see StepScreen's Execute cell — or KYRS's initialTaskId, from NUMA's
   *  "Open in KYRS" action). Absent for every other caller. */
  toolProps?: { initialProtocolId?: string; initialTaskId?: string };
  /** Cross-tool navigation (e.g. ORACLE's rings) — see ToolProps.onOpenTool. */
  onOpenTool?: (toolId: string, opts?: { initialTaskId?: string }) => void;
}) {
  const styles = useStyles();
  const toolId = toolIds[activeToolIndex] ?? toolIds[0];

  // Every tool with a deityAvatars.ts entry gets the shared AI module above
  // its own screen. The master ON/OFF toggle and all persistence now live
  // inside <AiModule/> (via DeityInterface) — off, it renders just the toggle.
  // SyQi's home stays untouched (SyqiTool already IS the deity interface —
  // see deityAvatars.ts).
  const deityEntry = toolId && toolId !== 'syqi' ? getDeityAvatar(toolId) : undefined;

  return (
    <View style={styles.panel}>
      <Atmosphere />
      {/* Duplicate title bar removed (Joel 2026-07-19): OlmpsHeader already
          names the tool; the ⓘ/✕/hexagon strip doubled it on every screen.
          Multi-tool cells keep a slim hexagon switcher only when there is
          actually more than one tool to switch between. */}
      {toolIds.length > 1 && (
        <View style={styles.switcherRow}>
          {toolIds.map((id, i) => (
            <Hexagon
              key={`${id}-${i}`}
              letter={(TOOL_NAMES[id] ?? id).charAt(0)}
              active={i === activeToolIndex}
              onPress={() => onSelectToolIndex(i)}
            />
          ))}
        </View>
      )}
      <View style={styles.body}>
        {deityEntry && (
          <View style={styles.deityWrap}>
            <DeityInterface toolId={toolId} />
          </View>
        )}
        {toolId ? (
          <ToolHost
            toolId={toolId}
            sector={sector}
            stage={stage}
            gear={gear}
            initialProtocolId={toolProps?.initialProtocolId}
            initialTaskId={toolProps?.initialTaskId}
            onOpenTool={onOpenTool}
          />
        ) : (
          <View style={styles.empty}>
            <Text style={styles.emptyText}>No tool assigned to this cell yet</Text>
          </View>
        )}
      </View>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    panel: {
      flex: 1,
      borderRadius: radii.panel,
      overflow: 'hidden',
      backgroundColor: 'transparent',
    },
    switcherRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 8,
      paddingHorizontal: 10,
      paddingVertical: 6,
    },
    deityWrap: { paddingHorizontal: 12, paddingTop: 8, paddingBottom: 4 },
    body: { flex: 1 },
    empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
    emptyText: { color: t.ivoryFaint, fontStyle: 'italic' },
  }),
);
